import { useState, useEffect, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { MESSAGES } from '@veriff/incontext-sdk'

import { Button } from '~/shadcn/ui/button/button'
import { Input } from '~/shadcn/ui/input'
import { Checkbox } from '~/shadcn/ui/form/checkbox'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/shadcn/ui/form/form'
import Analytics from '~/@lib_integrations/analytics/analytics'
import {
  capturePostHogProductEvent,
  PostHogProductEvent,
} from '~/@lib_integrations/posthog/postHogProductEvents'
import { useAppDialogsStore } from '~/@biz_app-dialogs/appDialogsStore'
import { IContentType } from '~/@api/generated-api-types-user/Api'
import { useUser } from '~/@biz_account/useUser/useUser'
import { useBrokerCode } from '~/@pages_auth/broker/useBrokerCode'
import { StepHeadline } from '../../_onboarding-stepper/StepHeadline'
import { VeriffRoot } from '../../identity-verification/veriff/VeriffRoot'
import { IdentityVerificationDialog } from '../../identity-verification/dialog-and-results/IdentityVerificationDialog'
import { IdentityVerificationUnderageDialog } from '../../identity-verification/dialog-and-results/IdentityVerificationUnderageDialog'
import { IdentityVerificationMismatchDialog } from '../../identity-verification/dialog-and-results/IdentityVerificationMismatchDialog'
import { useVeriff } from '../../identity-verification/veriff/useVeriff'
import { useIdentityVerificationStatus } from '../../identity-verification/useIdentityVerificationStatus'
import { Spinner } from '~/shadcn/ui/spinner'
import { Text } from '~/@mid_ui/typography/Text'
import { zodResolver } from '@hookform/resolvers/zod'
import { identityVerificationSchema } from './identityVerificationSchema'
import { FieldError, FieldLabel } from '~/shadcn/ui/field'
import { isSocialNetworkInAppWebView } from '~/@lib/utils/function.utils'
import { IdentityVerificationSocialInAppWebView } from '../veriff-in-app-web-view/IdentityVerificationSocialInAppWebView'

type Props = {
  callbackDoVeriff(): void
  /** BE `profile.identity.isUnderage` — Veriff approved, ale user ešte nemá 18 rokov. */
  isUnderage?: boolean
  /** BE status `reverification_mismatch` — nový doklad po expirácii nesedí s údajmi účtu (RČ / dátum narodenia). */
  isReverificationMismatch?: boolean
}

const IdentityVerification = (props: Props) => {
  const { setIdentitySubmittedStatus } = useIdentityVerificationStatus()
  const isWebView = isSocialNetworkInAppWebView()
  const { isVeriffPending, setIsVeriffPending, handleVeriffVerification } =
    useVeriff(isWebView)

  const { setShowContentDialog } = useAppDialogsStore()
  const {
    brokerCode,
    brokerCodeRememberedAt,
    clearBrokerCode,
    setBrokerCodeMutation,
  } = useBrokerCode()

  const { user, reloadUser } = useUser()

  const [showIdentifyVerificationDialog, setShowIdentifyVerificationDialog] =
    useState(false)
  // Auto-open po návrate z Veriffu (poller v Submitted prepne status → tento form sa mountne
  // už s isUnderage=true); ďalej sa otvára pri každom pokuse o Veriff.
  const [showUnderageDialog, setShowUnderageDialog] = useState(
    props.isUnderage === true
  )
  // Auto-open po návrate z Veriffu (rovnaký mechanizmus ako underage); „Skúsiť znova“ len zavrie,
  // user vyplní meno vo formulári pod modalom a ide na Veriff znova.
  const [showMismatchDialog, setShowMismatchDialog] = useState(
    props.isReverificationMismatch === true
  )
  const [gdprBiometricOpened, setGdprBiometricOpened] = useState(false)

  const form = useForm({
    resolver: zodResolver(identityVerificationSchema),
    defaultValues: {
      firstname: '',
      lastname: '',
      brokerCode: '',
      confirm: false,
    },
  })

  const handleBrokerCode = useCallback(
    async (code: string, rememberedAt: string) => {
      try {
        setBrokerCodeMutation.mutate({
          brokerCode: code,
          brokerCodeRememberedAt: rememberedAt,
        })
        clearBrokerCode()
      } catch (error) {
        console.error('Failed to apply broker code:', error)
      }
    },
    [clearBrokerCode, setBrokerCodeMutation]
  )

  useEffect(() => {
    if (brokerCode && brokerCodeRememberedAt) {
      handleBrokerCode(brokerCode, brokerCodeRememberedAt)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // verificationStatus is started or null
  const doVeriff = async (formData: {
    firstname: string
    lastname: string
  }) => {
    if (user == null || isVeriffPending) return

    if (props.isUnderage) {
      Analytics.track('veriff-verification-underage-blocked')
      setShowUnderageDialog(true)
      return
    }

    Analytics.track('veriff-verification-started')
    capturePostHogProductEvent(user, PostHogProductEvent.VERIFF_INITIATED, {
      is_in_app_web_view: isWebView,
      previous_verification_status:
        user.profile.identity?.verificationStatus ?? null,
    })

    setIsVeriffPending(true)

    if (typeof props.callbackDoVeriff === 'function') props.callbackDoVeriff()

    handleVeriffVerification({
      person: {
        givenName: formData.firstname,
        lastName: formData.lastname,
      },
      vendorData: user._id,
      onVeriffFinished: async (msg) => {
        setIsVeriffPending(false)

        if (msg === MESSAGES.SUBMITTED) {
          await setIdentitySubmittedStatus()
          await reloadUser()
        }

        if (msg === MESSAGES.CANCELED) {
          await reloadUser()
        }
      },
    })
  }

  return (
    <div className="flex flex-col gap-10">
      <div className="flex flex-col gap-5">
        <StepHeadline headingText="Overte svoju identitu" />
        {!isWebView && (
          <>
            <p className="font-medium">
              Pre zachovanie vašej bezpečnosti pri investovaní je nutné overenie
              vašej identity. Celý proces je bezpečný a zaberie vám len pár
              minút vášho času.
            </p>
            <p className="font-medium">
              Pre overenie identity používame bezpečnú aplikáciu Veriff.
            </p>
          </>
        )}

        {isWebView && (
          <p className="font-medium">
            V ďalšom kroku vás presunieme do bezpečnejšieho prostredia vášho
            prehliadača. Pošleme vám SMS s odkazom, cez ktorý sa prihlásite a
            overenie dokončíte tam.
          </p>
        )}
      </div>

      {!isWebView && (
        <>
          <VeriffRoot />

          <Form {...form}>
            <div className="flex flex-col gap-5">
              <FormField
                control={form.control}
                name="firstname"
                rules={{
                  required: true,
                  maxLength: 50,
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Meno</FormLabel>
                    <FormControl>
                      <Input {...field} autoComplete="given-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastname"
                rules={{
                  required: true,
                  maxLength: 50,
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Priezvisko</FormLabel>
                    <FormControl>
                      <Input {...field} autoComplete="family-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              name="confirm"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormItem className="gap-1">
                  <FieldLabel className="flex flex-row items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={isVeriffPending}
                    />
                    <Text className="text-sm font-medium leading-normal">
                      Súhlasím so spracovaním{' '}
                      <Button
                        type="button"
                        size="xs"
                        variant="link"
                        className="p-0 font-normal text-accent"
                        onClick={(event) => {
                          event.preventDefault()
                          if (!gdprBiometricOpened) {
                            Analytics.track('link-privacy-marketing')
                            setGdprBiometricOpened(true)
                          }
                          setShowContentDialog(
                            IContentType.DialogPrivacyBiometry
                          )
                        }}>
                        biometrických údajov
                      </Button>
                    </Text>
                  </FieldLabel>
                  {fieldState.error && (
                    <FieldError>{fieldState.error.message}</FieldError>
                  )}
                </FormItem>
              )}
            />
            <div className="flex flex-col lg:flex-row gap-3">
              <Button
                variant="outline"
                type="button"
                className="w-full lg:w-auto"
                disabled={isVeriffPending}
                onClick={() => {
                  setShowIdentifyVerificationDialog(true)
                }}>
                Overiť identitu neskôr
              </Button>
              <Button
                variant="accent"
                type="submit"
                className="w-full lg:w-auto"
                disabled={isVeriffPending}
                onClick={form.handleSubmit(doVeriff)}>
                Overiť identitu cez Veriff
                {isVeriffPending && <Spinner className="size-6" />}
              </Button>
            </div>
          </Form>
          {showIdentifyVerificationDialog && (
            <IdentityVerificationDialog
              onClose={() => setShowIdentifyVerificationDialog(false)}
            />
          )}
          {showUnderageDialog && (
            <IdentityVerificationUnderageDialog
              onClose={() => setShowUnderageDialog(false)}
            />
          )}
          {showMismatchDialog && (
            <IdentityVerificationMismatchDialog
              onClose={() => setShowMismatchDialog(false)}
            />
          )}
        </>
      )}

      {isWebView && <IdentityVerificationSocialInAppWebView />}
    </div>
  )
}

export { IdentityVerification }
